import * as React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { PanelMarker } from '../../models/PanelMarker';
import { MathUtils } from '../../utils';
import { MUTED_PALETTE } from './chartPalette';
import { bloodDropLabel } from './bloodDropLabel';

const blood_drop_current = require('../../assets/imgs/blood_drop_black.png');
const blood_drop_previous = require('../../assets/imgs/blood_drop_blue_50.png');

type Props = {
    panelMarker: PanelMarker;
    chartContainerClass: string;
};

type State = {
    panelMarker: PanelMarker;
    currentChartData: number;
    previousChartData: number;
}

// Use this flag to see actual percentFromMean values on chart
const DEBUG: boolean = false;
const DELAY: number = 2500;

export class EGFRReferenceRangeChart extends React.Component<Props, State> {
    state: State = {
        panelMarker: this.props.panelMarker,
        currentChartData: undefined,
        previousChartData: undefined
    };

    componentWillReceiveProps(nextProps) {
        const { panelMarker } = nextProps;
        this.setState({
            panelMarker,
            currentChartData: this.createChartData(panelMarker),
            previousChartData: this.createChartData(panelMarker.previousPanelMarker)
        });
    }

    componentDidMount() {
        this.populateChartData();
    }
/* This is the original createChartData before we added psaFree and psaFrePercent that both useEGFRChart = true
    createChartData(panelMarker: PanelMarker) {
        if (!panelMarker || panelMarker.isBlank()) {
            return undefined;
        }
        return MathUtils.round(panelMarker.eGFRChartValue(), panelMarker.field.precision)
    }
*/

//This is the new createChartData to account for psaFree and psaFreePercent that both use the EGFR Chart type
    createChartData(panelMarker: PanelMarker) {
        if (!panelMarker || panelMarker.isBlank()) {
            return undefined;
        }

        let chartValue: number | undefined;

        // 1) Keep original behavior for true EGFR markers
        if (panelMarker.isEGFR && panelMarker.isEGFR()) {
            chartValue = panelMarker.eGFRChartValue();
        }
        // 2) PSA markers that should use this visual style
        else if (
            panelMarker.attribute === 'psaFree' ||
            panelMarker.attribute === 'psaFreePercent'
        ) {
            chartValue = this.computePSAChartValue(panelMarker);
        } else {
            // Fallback: nothing special – just bail
            return undefined;
        }

        if (chartValue == null) {
            return undefined;
        }

        return MathUtils.round(chartValue, panelMarker.field.precision);
    }
    //This is the new computerPSAChartVlue for making sure the blood drops for psaFree and psaFreePercent fall in the correct place
    private computePSAChartValue(pm: PanelMarker): number {
        if (!pm || pm.isBlank() || !pm.range || pm.value == null) {
            return undefined;
        }

        const r = pm.range;
        const v = pm.value;

        const {
            alarmMin,
            labMin,
            optimalMin,
            optimalMax,
            labMax,
            alarmMax
        } = r;

        // If key points are missing, fall back to a simple linear map alarmMin–alarmMax → 0–300
        if (
            alarmMin == null ||
            labMin == null ||
            optimalMin == null ||
            optimalMax == null ||
            labMax == null ||
            alarmMax == null
        ) {
            const min = alarmMin ?? v;
            const max = alarmMax ?? v;
            if (min === max) {
                return 150;
            }
            return ((v - min) / (max - min)) * 300;
        }

        const SEG = 33.33;

        // Segment 1: [0, alarmMin] → 0–33.33
        if (v <= alarmMin) {
            return (v / alarmMin) * SEG;
        }

        // Segment 2: (alarmMin, labMin] → 33.33–66.67
        if (v <= labMin) {
            return ((v - alarmMin) / (labMin - alarmMin)) * SEG + SEG;
        }

        // Segment 3: (labMin, optimalMin] → 66.67–100
        if (v <= optimalMin) {
            return ((v - labMin) / (optimalMin - labMin)) * SEG + 2 * SEG;
        }

        // Segment 4: (optimalMin, optimalMax] → 100–133.33
        if (v <= optimalMax) {
            return ((v - optimalMin) / (optimalMax - optimalMin)) * SEG + 3 * SEG;
        }

        // Segment 5: (optimalMax, labMax] → 133.33–200 (two segments tall)
        if (v <= labMax) {
            return ((v - optimalMax) / (labMax - optimalMax)) * (2 * SEG) + 4 * SEG;
        }

        // Segment 6: (labMax, alarmMax] → 200–233.33
        if (v <= alarmMax) {
            return ((v - labMax) / (alarmMax - labMax)) * SEG + 6 * SEG;
        }

        /* Segment 7: > alarmMax → go beyond 233.33; adjustChartData will clamp to 300
        const spanAbove = alarmMax - labMax || 1;
        const extra = ((v - alarmMax) / spanAbove) * SEG;
        return 7 * SEG + extra;*/

       // Segment 7: > alarmMax
        // From alarmMax to alarmMax + (alarmMax - labMax), keep scaling like the other segments.
        // Above alarmMax + (alarmMax - labMax), peg at the last tick (266.67)
        // so the blood drop stays clearly inside the chart.

        const span = alarmMax - labMax;

        if (span > 0) {
            const upper = alarmMax + span;

            if (v <= upper) {
                const extra = ((v - alarmMax) / span) * SEG;   // 0 → SEG
                const y = 7 * SEG + extra;                     // 233.33 → 266.67
                return y;
            }
        }

        // If span <= 0 (bad data) OR v > alarmMax + span, pin at 266.67
        return 266.67;

    }

    populateChartData() {
        const { panelMarker } = this.state;
        setTimeout(()=>{
            this.setState({
                currentChartData: this.createChartData(panelMarker),
                previousChartData: this.createChartData(panelMarker.previousPanelMarker)
            });
        }, DELAY);  
    }

    adjustChartData(chartData: number): number {
        let value: number = chartData;
        if (chartData < 0) {
            value = 0;
        } else if (value > 300) {
            value = 300;
        }
        return value;
    }

    render() {
        const { chartContainerClass } = this.props;
        const { panelMarker } = this.state;
        const pm = panelMarker;
        // Render when the current OR the previous test has a value, so a
        // "previous-only" comparison marker still plots its previous (blue) drop.
        if (!panelMarker.hasValue() && !(pm.previousPanelMarker && pm.previousPanelMarker.hasValue())) {
            return null;
        }

        const { green: GREEN, yellow: YELLOW, orange: ORANGE, red: RED } = MUTED_PALETTE;
        const GRID_LINE_COLOR = '#CDD7EA';
        const BAR_COLOR = 'rgba(255, 255, 255, 0)';

        // If the value goes "off the chart" make it show at the very beginning or end.
        let currentChartData = this.adjustChartData(this.state.currentChartData);
        let previousChartData = this.adjustChartData(this.state.previousChartData);

        // For a "previous-only" marker (no current value, previous present) give
        // the CURRENT series the SAME in-range point as the previous drop and
        // hide its (black) label. Leaving it off-chart makes Highcharts shift
        // the visible blue drop downward off the bar; matching both series'
        // points keeps it centered like every normal two-drop row.
        const isPreviousOnly = !pm.hasValue() && !!pm.previousPanelMarker && pm.previousPanelMarker.hasValue();
        const currentSeriesData = isPreviousOnly ? previousChartData : currentChartData;

        let offset = -12;

        const options: Highcharts.Options = {
            chart: {
                type: 'bar',
                marginTop: 20,
                marginBottom: 20
            },
            // Overlay CURRENT and PREVIOUS series on the same row so the
            // previous blood drop renders at the same vertical position as
            // the current one.
            plotOptions: {
                bar: { grouping: false, pointPlacement: 0 },
            },
            title: {
                text: ''
            },
            xAxis: [{
                labels: {
                    enabled: false
                },
                tickLength: 0
            }],
            yAxis: {
                title: {
                    text: ''
                },
                labels: {
                    enabled: true,
                    formatter: function () {
                        const self: any = this;
                        const digits = panelMarker.field.precision;
                        if (DEBUG) {
                            return self.value;
                        }
                        switch (self.value) {
                            case 33.33:
                                return `${MathUtils.round(pm.range.alarmMin, digits)}`;
                            case 66.67:
                                return `${MathUtils.round(pm.range.labMin, digits)}`;   
                            case 100:
                                return `${MathUtils.round(pm.range.optimalMin, digits)}`;  
                            case 133.33:
                                return `${MathUtils.round(pm.range.optimalMax, digits)}`;  
                            case 166.67:
                                return `${MathUtils.round(pm.range.optimalMax + pm.range.stdDev, digits)}`;     
                            case 200:
                                return `${MathUtils.round(pm.range.labMax, digits)}`;
                            case 233.33:
                                return `${MathUtils.round(pm.range.alarmMax, digits)}`;                                
                            default:
                                return '';
                        }
                    } 
                },
                min: 0,
                max: 300,
                tickPositions: [0, 33.33, 66.67, 100, 133.33, 166.67, 200, 233.33, 266.67],
                gridLineWidth: 1, // removes grid lines between bands if set to 0
                gridLineColor: GRID_LINE_COLOR,
                plotBands: [{
                    label: {
                        // text: 'Alarm',
                        align: 'center',
                        y: -5
                    },
                    from: 0,
                    to: 33.33,
                    color: RED
                },
                {
                    label: {
                        // text: '< Lab',
                        align: 'center',
                        y: -5
                    },
                    color: ORANGE,
                    from: 33.33,
                    to: 66.67
                },
                {
                    label: {
                        // text: 'Lab',
                        align: 'center',
                        y: -5
                    },
                    from: 66.67,
                    to: 100,
                    color: YELLOW
                },
                {
                    label: {
                        // text: 'Optimal',
                        align: 'center',
                        y: -5
                    },
                    from: 100,
                    to: 300,
                    color: GREEN
                }
                ]
            },
            tooltip: {
                followPointer: true,
                followTouchMove: true,
                padding: 5,
                useHTML: true,
                formatter: function () {
                    var currentValue = pm && pm.hasValue() ? pm.displayValue() : 'N/A';
                    var previousValue = pm && pm.previousPanelMarker && pm.previousPanelMarker.hasValue() ? pm.previousPanelMarker.displayValue() : 'N/A';
                    return  `<b> Current: ${currentValue}<br>Previous: ${previousValue}</b>`;              
                }
            },
            series: [{
                name: 'CURRENT',
                type: 'bar',
                color: BAR_COLOR,
                borderWidth: 0,
                pointWidth: 10,
                data: [currentSeriesData],
                dataLabels: {
                    enabled: !isPreviousOnly,
                    allowOverlap: true,
                    useHTML: true,
                    verticalAlign: 'middle',
                    x: offset,
                    format: bloodDropLabel(blood_drop_current)
                }
            },
            {
                name: 'PREVIOUS',
                type: 'bar',
                color: BAR_COLOR,
                borderWidth: 0,
                pointWidth: 10,
                data: [previousChartData],
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    useHTML: true,
                    verticalAlign: 'middle',
                    x: offset,
                    format: bloodDropLabel(blood_drop_previous)
                }
            }],
            legend: {
                enabled: false
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            }
        };

        return (
            <HighchartsReact 
                options={options}
                highcharts={Highcharts}
                containerProps = {{ className: chartContainerClass }}
            />
        );
    }
}